/**
 * 任意テキスト / SSML の音声プレビューの副作用を担うフック。
 *
 * 分割前は `FreeTextFields` 内のローカル state (`previewing` / `previewError` / `previewUrl`)、
 * `previewAudioRef`、および再生速度反映の `useEffect` として実装されていたものを、
 * ロジックを変えずそのまま切り出したもの。
 *
 * 要件 2: プレビュー音声にも再生速度を反映するため、<audio> 要素に ref を張り、
 * playbackRate プロパティ変化時に手動で設定する。src が変わったときや
 * 再生開始時にリセットされてしまうことがあるため、呼び出し側の `onLoadedMetadata` でも
 * 再適用する (ref + onLoadedMetadata の両方で反映)。
 *
 * ## 生成できたらそのまま鳴らす (Issue #482)
 *
 * 「▶ この文面を試聴」は生成後に `<audio controls>` を出すだけだったため、
 * **音を聴くまでに 2 クリック** (試聴ボタン → プレーヤーの再生ボタン) 必要だった。
 * 一方「▶ 多言語を通しで試聴」「この放送地点を通しで試聴」は `playSequential()` が
 * 直接 `play()` するため 1 クリックで鳴る。同じ「試聴」なのに操作回数が違うと
 * 「押したのに鳴らない (壊れている)」と受け取られるため、
 * **生成完了と同時に自動再生** して通し試聴と操作感を揃える
 * (言語セレクトでどの言語を選んだ場合も同じ経路なので全言語で 1 クリックになる)。
 *
 * 自動再生がブラウザにブロックされた場合は例外にせず、
 * 「プレーヤーの再生ボタンを押す」導線を案内する (音声そのものは生成できているため)。
 */
import { useEffect, useRef, useState } from 'react';

import type { PreviewSsmlHandler, PreviewSsmlResult } from '../types';
import { describeApiErrorMessage } from '../../../utils/apiErrorMessage';

/**
 * 自動再生がブラウザにブロックされたときの案内文 (Issue #482)。
 *
 * 生成自体は成功しているので「失敗」ではなく次の操作を示す文言にする。
 */
export const PREVIEW_AUTOPLAY_BLOCKED_MESSAGE =
  '自動再生がブロックされました。プレーヤーの再生ボタンを押してください';

export interface UseAudioPreviewResult {
  /** プレビュー音声の生成中フラグ */
  previewing: boolean;
  /** プレビュー生成失敗時のエラーメッセージ (null = エラーなし) */
  previewError: string | null;
  /** 生成済みプレビュー音声の object URL (blob:)。null = 未生成 */
  previewUrl: string | null;
  /** プレビュー <audio> 要素への ref (playbackRate を手動設定するために使う) */
  previewAudioRef: React.RefObject<HTMLAudioElement | null>;
  /** 実効再生速度 (範囲 0.5〜2.0 外 / 未指定は 1.0 = 等倍) */
  effectivePlaybackRate: number;
  /**
   * 直近のプレビュー結果 (Issue #439)。
   *
   * サーバがレンダリングした文面 (`renderedSsml`)、採用された遅延パターン行
   * (`matchedPatternIndex`)、未展開プレースホルダ (`unresolvedTokens`) を含む。
   * `content` を指定せず従来形 (text / ssml) で呼んだ場合は `url` のみが入る。
   */
  lastResult: PreviewSsmlResult | null;
  /**
   * プレビュー音声を生成する (呼び出し側で入力内容とプレビュー可否を判定してから呼ぶ)。
   * 成功時は結果を、失敗時は `null` を返す (エラーは `previewError` に反映される)。
   */
  startPreview: (input: Parameters<PreviewSsmlHandler>[0]) => Promise<PreviewSsmlResult | null>;
}

export function useAudioPreview({
  onPreviewSsml,
  playbackRate,
}: {
  onPreviewSsml?: PreviewSsmlHandler;
  /**
   * 親コンテンツの `playback_rate` (要件 2)。
   * `undefined` / `1` は等倍再生。範囲 (0.5〜2.0) 外の値は 1.0 に落として無視する。
   */
  playbackRate?: number;
}): UseAudioPreviewResult {
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<PreviewSsmlResult | null>(null);

  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  /*
   * アンマウント時の object URL 解放 (Issue #439)。
   *
   * プレビュー部品 (`AudioPreviewField`) は各コンテンツ / 各遅延パターン行に
   * **複数同時に存在する** ようになったため、生成した blob URL を解放せずに
   * アンマウントすると、モーダルの開閉を繰り返すたびにメモリを消費し続ける。
   * `startPreview` 内の revoke (差し替え時) に加えて、アンマウント時にも解放する。
   *
   * `previewUrl` を deps に入れず ref で追跡するのは、「URL が変わるたびに
   * cleanup が走って再生中の音声が無効化される」のを避けるため
   * (差し替え時の解放は startPreview 側が責任を持つ)。
   *
   * ref への書き込みはレンダー中ではなく `useEffect` 内で行う。
   * レンダー中に ref を変更すると React の並行レンダリング下で
   * 破棄されたレンダー結果の値が残り得るため (`react-hooks/refs`)。
   */
  const previewUrlRef = useRef<string | null>(null);
  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);
  useEffect(
    () => () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    },
    [],
  );
  const effectivePlaybackRate =
    typeof playbackRate === 'number' && playbackRate >= 0.5 && playbackRate <= 2.0
      ? playbackRate
      : 1.0;
  useEffect(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.playbackRate = effectivePlaybackRate;
    }
  }, [effectivePlaybackRate, previewUrl]);

  /*
   * 自動再生の要求カウンタ (Issue #482)。
   *
   * `<audio>` の `src` は `previewUrl` (state) から描画されるため、
   * `startPreview()` の中で `previewAudioRef.current.play()` を呼んでも
   * **まだ古い src (または要素そのものが未マウント)** の状態で鳴らしてしまう。
   * そこで「再生してほしい」という要求だけをカウンタで立て、
   * src が DOM に反映された **コミット後** (`useEffect`) に再生する。
   *
   * `previewUrl` を依存配列にしないのは、同じ URL が続けて返ってきた場合
   * (サーバが同一音源を返す / テストのモック) でも再生をやり直したいため。
   * カウンタなら毎回必ず変化するので「押したのに鳴らない」が起きない。
   */
  const [playRequestId, setPlayRequestId] = useState(0);
  useEffect(() => {
    // 初期描画 (まだ一度も試聴していない) では鳴らさない。
    if (playRequestId === 0) return;
    const audio = previewAudioRef.current;
    if (!audio) return;
    /*
     * 同じ要素を使い回すため、2 回目以降は先頭へ巻き戻してから鳴らす
     * (前回の再生位置が末尾のままだと「押しても鳴らない」ように見える)。
     * jsdom など `currentTime` を扱えない環境でも試聴処理を止めない。
     */
    try {
      audio.currentTime = 0;
    } catch {
      // 巻き戻せない環境では現在位置から再生する (再生自体は継続する)。
    }
    const started = audio.play();
    /*
     * `play()` は Promise を返す実装 (ブラウザ) と返さない実装 (jsdom 等) がある。
     * 自動再生ブロックは「生成は成功したが鳴らせなかった」だけなので、
     * 生成エラーとは区別して次の操作 (プレーヤーの再生ボタン) を案内する。
     */
    if (started && typeof started.catch === 'function') {
      started.catch(() => setPreviewError(PREVIEW_AUTOPLAY_BLOCKED_MESSAGE));
    }
  }, [playRequestId]);

  const startPreview = async (
    input: Parameters<PreviewSsmlHandler>[0],
  ): Promise<PreviewSsmlResult | null> => {
    if (!onPreviewSsml) return null;
    setPreviewError(null);
    setPreviewing(true);
    try {
      // 直前のプレビュー URL は解放しておく
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const result = await onPreviewSsml(input);
      setPreviewUrl(result.url);
      setLastResult(result);
      /*
       * Issue #482: 生成できたらそのまま鳴らす (通し試聴と同じ 1 クリック操作)。
       * 実際の `play()` は src が DOM へ反映された後 (上の useEffect) で行う。
       */
      setPlayRequestId((prev) => prev + 1);
      return result;
    } catch (err) {
      setPreviewError(describeApiErrorMessage(err, '音声プレビューの生成に失敗しました'));
      // 生成に失敗したら前回の診断情報 (文面 / 未展開トークン) を残さない。
      // 古い `rendered_ssml` が新しいエラーの隣に並ぶと誤解を招くため。
      setLastResult(null);
      return null;
    } finally {
      setPreviewing(false);
    }
  };

  return {
    previewing,
    previewError,
    previewUrl,
    previewAudioRef,
    effectivePlaybackRate,
    lastResult,
    startPreview,
  };
}
